import { useMemo } from "react";
import { Copy, Check } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";

const flatten = (s) => String(s || "").replace(/\n/g, " ").trim();

export const ShareDialog = ({ open, onOpenChange, mode, index, slide }) => {
  const { t, lang } = useApp();

  const { ogUrl, waUrl, liUrl, title } = useMemo(() => {
    const origin = window.location.origin;
    const to = `/deck/${mode}`;
    const title = flatten(slide?.title) || "iFotoLife";
    const desc = flatten(slide?.lead || slide?.body || t.home.taglines?.[0]);
    const img = slide?.image || slide?.mockImage || "";
    const params = new URLSearchParams({
      to, o: origin, lang, slide: String(index), t: title, d: desc, img,
    });
    const ogUrl = `${origin}/api/og?${params.toString()}`;
    const waUrl = `https://wa.me/?text=${encodeURIComponent(`${title} — iFotoLife\n${ogUrl}`)}`;
    const liUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(ogUrl)}`;
    return { ogUrl, waUrl, liUrl, title };
  }, [mode, index, slide, lang, t]);

  const copy = () => {
    navigator.clipboard?.writeText(ogUrl).then(
      () => toast.success(t.ui.linkCopied),
      () => toast.error("Error")
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="border-white/10 bg-navy-900 text-cream sm:max-w-md"
        data-testid="share-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white">{t.ui.shareTitle}</DialogTitle>
          <DialogDescription className="text-cream/60">{t.ui.shareText}</DialogDescription>
        </DialogHeader>

        {/* Preview card (mirrors the OG preview) */}
        <div className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.03]">
          <div className="relative h-32 w-full overflow-hidden bg-navy-700">
            {(slide?.image || slide?.mockImage) && (
              <img src={slide.image || slide.mockImage} alt="" className="h-full w-full object-cover" />
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-navy-900 to-transparent" />
          </div>
          <div className="p-4">
            <div className="font-serif text-base text-white line-clamp-1">{title}</div>
            <div className="text-xs text-cream/50">ifotolife · {t.modeMeta[mode]}</div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <a
            href={waUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-whatsapp"
            className="flex items-center justify-center gap-2 rounded-xl bg-[#25D366] py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
            </svg>
            WhatsApp
          </a>
          <a
            href={liUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="share-linkedin"
            className="flex items-center justify-center gap-2 rounded-xl bg-[#0A66C2] py-3 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
          >
            <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.225 0z" />
            </svg>
            LinkedIn
          </a>
        </div>

        <button
          onClick={copy}
          data-testid="share-copy"
          className="flex items-center justify-center gap-2 rounded-xl border border-champagne/40 bg-champagne/10 py-3 text-sm font-medium text-champagne transition-colors hover:bg-champagne/20"
        >
          <Copy className="h-4 w-4" strokeWidth={1.5} /> {t.ui.copyLink}
        </button>
      </DialogContent>
    </Dialog>
  );
};
